/* ==========================================================================
   TOGETHER · Event site — lógica de interface, compra e analytics
   Sem dependências. Carregado com `defer`.

   Módulos:
     TP.track     → analytics (Shopify Customer Events: Shopify.analytics.publish)
     TP.checkout  → compra direta via cart permalink (/cart/VARIANT:QTY → checkout)
     TP.ui        → countdown, header, sticky CTA, fachadas de vídeo/mapa, FAQ, cookies
   ========================================================================== */
(() => {
  'use strict';

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection && navigator.connection.saveData;
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';

  /* ------------------------------------------------------------------ */
  /* Analytics                                                           */
  /* Os eventos são publicados para o sandbox de Customer Events do     */
  /* Shopify, onde o pixel personalizado (docs/custom-pixel.js) os envia */
  /* para GA4 / Meta / TikTok — só depois do consentimento do visitante. */
  /* ------------------------------------------------------------------ */
  const sent = new Set();
  function track(name, data = {}, { once = false } = {}) {
    if (once) {
      if (sent.has(name)) return;
      sent.add(name);
    }
    const payload = Object.assign({ currency }, data);
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('tp:' + name, payload);
      }
    } catch (e) { /* analytics nunca pode partir a compra */ }
    document.dispatchEvent(new CustomEvent('tp:track', { detail: { name, data: payload } }));
  }

  /* ------------------------------------------------------------------ */
  /* Checkout: escolher bilhete → quantidade → checkout Shopify           */
  /* ------------------------------------------------------------------ */
  function fmt(value) {
    try {
      return new Intl.NumberFormat('pt-PT', { style: 'currency', currency, maximumFractionDigits: value % 1 ? 2 : 0 }).format(value);
    } catch (e) {
      return value + ' €';
    }
  }

  function initTicket(card) {
    const input = $('[data-tp-qty-input]', card);
    const buy = $('[data-tp-buy]', card);
    const total = $('[data-tp-total]', card);
    if (!buy || !input) return;
    const price = parseFloat(card.dataset.price || '0');
    const min = parseInt(input.min || '1', 10);
    const max = parseInt(input.max || '10', 10);
    const minus = $('[data-step="-1"]', card);
    const plus = $('[data-step="1"]', card);

    const clamp = (n) => Math.min(max, Math.max(min, isNaN(n) ? min : n));
    const render = () => {
      const q = clamp(parseInt(input.value, 10));
      input.value = q;
      if (minus) minus.disabled = q <= min;
      if (plus) plus.disabled = q >= max;
      if (total) total.textContent = q > 1 && price ? fmt(price * q) : '';
    };

    $$('[data-step]', card).forEach((b) =>
      b.addEventListener('click', () => {
        input.value = clamp(parseInt(input.value, 10) + parseInt(b.dataset.step, 10));
        render();
      })
    );
    input.addEventListener('change', render);
    render();

    buy.addEventListener('click', () => {
      const variant = card.dataset.variantId;
      if (!variant) return;
      const qty = clamp(parseInt(input.value, 10));
      const item = {
        item_id: variant,
        item_name: card.dataset.productTitle || card.dataset.label,
        item_variant: card.dataset.label,
        price,
        quantity: qty,
      };
      const value = Math.round(price * qty * 100) / 100;
      if (card.hasAttribute('data-vip')) track('vip_interest', { value, items: [item], action: 'checkout' });
      track('add_to_cart', { value, items: [item] });
      track('initiate_checkout', { value, items: [item] });

      buy.classList.add('is-loading');
      buy.setAttribute('aria-busy', 'true');
      // Cart permalink: substitui o carrinho e abre o checkout seguro do Shopify (sem conta obrigatória).
      const url = `${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/${encodeURIComponent(variant)}:${qty}`;
      // Pequeno atraso para os pixels enviarem os eventos antes de sair da página.
      setTimeout(() => { window.location.href = url; }, 120);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Countdown                                                            */
  /* ------------------------------------------------------------------ */
  function initCountdown(el) {
    const target = Date.parse(el.dataset.tpCountdown);
    if (isNaN(target)) return;
    const live = $('[data-tp-countdown-live]', el.parentElement);
    const units = { d: $('[data-u="d"]', el), h: $('[data-u="h"]', el), m: $('[data-u="m"]', el), s: $('[data-u="s"]', el) };
    const pad = (n) => String(n).padStart(2, '0');
    let timer;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        el.hidden = true;
        if (live) live.hidden = false;
        clearInterval(timer);
        return;
      }
      const s = Math.floor(diff / 1000);
      units.d.textContent = pad(Math.floor(s / 86400));
      units.h.textContent = pad(Math.floor((s % 86400) / 3600));
      units.m.textContent = pad(Math.floor((s % 3600) / 60));
      units.s.textContent = pad(s % 60);
    };
    tick();
    timer = setInterval(tick, 1000);
  }

  /* ------------------------------------------------------------------ */
  /* Header / sticky CTA / WhatsApp                                      */
  /* ------------------------------------------------------------------ */
  function initHeader() {
    const header = $('[data-tp-header]');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function initSticky() {
    const bar = $('[data-tp-sticky]');
    if (!bar || !('IntersectionObserver' in window)) return;
    document.body.classList.add('has-sticky');
    const hero = $('.tp-hero');
    const tickets = $('#bilhetes');
    const footer = $('.tp-footer');
    const state = { pastHero: !hero, tickets: false, footer: false };
    const update = () => {
      const show = state.pastHero && !state.tickets && !state.footer;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      $$('a, button', bar).forEach((a) => (show ? a.removeAttribute('tabindex') : a.setAttribute('tabindex', '-1')));
      document.body.classList.toggle('sticky-on', show);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.target === hero) state.pastHero = !e.isIntersecting;
        if (e.target === tickets) state.tickets = e.isIntersecting;
        if (e.target === footer) state.footer = e.isIntersecting;
      });
      update();
    }, { threshold: 0, rootMargin: '0px 0px -30% 0px' });
    [hero, tickets, footer].forEach((t) => t && io.observe(t));
    update();
  }

  /* ------------------------------------------------------------------ */
  /* Media: vídeo do hero, fachadas de vídeo e mapa (carregam ao clicar) */
  /* ------------------------------------------------------------------ */
  function initHeroVideo() {
    const v = $('[data-tp-hero-video]');
    if (!v || reduceMotion || saveData) return;
    const start = () => {
      v.preload = 'auto';
      v.play().then(() => v.classList.add('is-playing')).catch(() => {});
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  function initFacades(root = document) {
    $$('[data-tp-video-facade]', root).forEach((btn) =>
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-active')) return;
        const tpl = $('template', btn);
        if (!tpl) return;
        btn.classList.add('is-active');
        btn.appendChild(tpl.content.cloneNode(true));
        const video = $('video', btn);
        if (video) video.play().catch(() => {});
        track('video_play', { label: btn.getAttribute('aria-label') });
      })
    );
    $$('[data-tp-embed]', root).forEach((btn) =>
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-active')) return;
        const f = document.createElement('iframe');
        f.src = btn.dataset.tpEmbed;
        f.title = btn.dataset.tpEmbedTitle || btn.getAttribute('aria-label') || 'Conteúdo incorporado';
        f.allow = 'autoplay; fullscreen; picture-in-picture';
        f.loading = 'lazy';
        f.referrerPolicy = 'strict-origin-when-cross-origin';
        btn.classList.add('is-active');
        btn.replaceChildren(f);
      })
    );
  }

  /* ------------------------------------------------------------------ */
  /* Tracking de secções, cliques e WhatsApp                            */
  /* ------------------------------------------------------------------ */
  function initTracking() {
    const map = { view_content: 'view_content', view_tickets: 'view_tickets', vip_interest: 'vip_interest' };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const name = map[e.target.dataset.tpView];
          if (name) track(name, name === 'vip_interest' ? { action: 'view' } : {}, { once: true });
          io.unobserve(e.target);
        });
      }, { threshold: 0.35 });
      $$('[data-tp-view]').forEach((el) => io.observe(el));
    }
    document.addEventListener('click', (ev) => {
      const wa = ev.target.closest('[data-tp-wa]');
      if (wa) {
        track('whatsapp_click', { placement: wa.dataset.tpWa });
        if (wa.dataset.tpWa.startsWith('vip')) track('vip_interest', { action: 'whatsapp' });
        return;
      }
      const t = ev.target.closest('[data-tp-track]');
      if (t) track('cta_click', { placement: t.dataset.tpTrack });
      const faq = ev.target.closest('[data-tp-faq]');
      if (faq) track('faq_open', { question: faq.textContent.trim().slice(0, 80) });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cookies: abre as preferências do banner nativo do Shopify           */
  /* ------------------------------------------------------------------ */
  function initCookiePrefs() {
    $$('[data-tp-cookie-prefs]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function') {
          window.privacyBanner.showPreferences();
        } else if (window.Shopify && typeof Shopify.loadFeatures === 'function') {
          Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
            if (window.privacyBanner) window.privacyBanner.showPreferences();
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Reveal ao fazer scroll (apenas se o utilizador aceita movimento)    */
  /* ------------------------------------------------------------------ */
  function initReveal() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;
    const els = $$('.tp-sec-head, .tp-ticket, .tp-vip-tier, .tp-artist, .tp-realm, .tp-gallery__item, .tp-location__card, .tp-about__media');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top > window.innerHeight) {
        el.classList.add('tp-io');
        io.observe(el);
      }
    });
  }

  function init() {
    $$('[data-tp-ticket]').forEach(initTicket);
    $$('[data-tp-countdown]').forEach(initCountdown);
    initHeader();
    initSticky();
    initHeroVideo();
    initFacades();
    initTracking();
    initCookiePrefs();
    initReveal();
  }

  window.TP = { track };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Editor do tema: reinicializa quando uma secção é recarregada.
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-tp-ticket]', e.target).forEach(initTicket);
    $$('[data-tp-countdown]', e.target).forEach(initCountdown);
    initFacades(e.target);
  });
})();
